import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bookmark, Play } from "lucide-react";
import { useAudio } from "../contexts/AudioContext";
import ChantStreakCard from "../components/ChantStreakCard";
import WalletCard from "../components/WalletCard";
import MembershipCard from "../components/MembershipCard";
import FollowedDeitiesCard from "../components/FollowedDeitiesCard";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const { playName } = useAudio();

  useEffect(() => {
    if (user) api.get("/bookmarks").then((r) => setBookmarks(r.data.items));
  }, [user]);

  if (loading) return <div className="p-10 text-[#63534E]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "supplier") return <Navigate to="/supplier/dashboard" replace />;
  if (user.role === "ngo") return <Navigate to="/ngo/dashboard" replace />;

  return (
    <div data-testid="dashboard-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">Namaste, {user.name}</div>
      <h1 className="font-serif text-5xl text-[#4A0E17] mt-2">Your sacred space</h1>

      <div className="mt-10">
        <ChantStreakCard />
      </div>
      <div className="mt-6"><MembershipCard kind="seva_club" /></div>
      <div className="mt-6"><WalletCard /></div>
      <div className="mt-6"><FollowedDeitiesCard /></div>

      <div className="grid md:grid-cols-3 gap-5 mt-6">
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <div className="overline">Bookmarks</div>
          <div className="font-serif text-4xl text-[#4A0E17] mt-2">{bookmarks.length}</div>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <div className="overline">Role</div>
          <div className="font-serif text-2xl text-[#4A0E17] mt-2 capitalize">{user.role}</div>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <div className="overline">Member since</div>
          <div className="font-serif text-2xl text-[#4A0E17] mt-2">{new Date(user.created_at).toLocaleDateString()}</div>
        </div>
      </div>

      <section className="mt-16">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-3xl text-[#4A0E17] flex items-center gap-3"><Bookmark className="w-6 h-6 text-[#E65100]" /> Bookmarked Names</h2>
          <Link to="/names" className="btn-ghost-gold text-sm">Browse all →</Link>
        </div>
        {bookmarks.length === 0 ? (
          <div className="mt-8 bg-[#FFFDF9] sacred-border rounded-2xl p-10 text-center text-[#63534E]">
            No bookmarks yet. Browse the <Link to="/names" className="text-[#E65100] font-semibold">1008 Names</Link> and save your favorites.
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {bookmarks.map((b) => b.name && (
              <div data-testid={`bookmark-item-${b.name.number}`} key={b.id} className="bg-[#FFFDF9] sacred-border rounded-2xl p-5">
                <div className="font-mono text-xs text-[#8C6D3B]">#{String(b.name.number).padStart(4, "0")}</div>
                <div className="font-devanagari text-xl text-[#4A0E17] mt-2">{b.name.sanskrit}</div>
                <div className="font-serif text-lg text-[#4A0E17]">{b.name.english}</div>
                <p className="text-sm text-[#63534E] mt-2 line-clamp-2">{b.name.meaning}</p>
                <button onClick={() => playName(b.name)} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#E65100]">
                  <Play className="w-4 h-4" /> Chant
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
